import type { Id } from '@/convex/_generated/dataModel';
import type { ChannelComposerSendPayload } from './channel-composer-types';
import type { SendErrorCode } from './send-error';

/**
 * `sending` = the mutation has not resolved (including while offline, when the
 * Convex client queues it); `sent` = the server accepted it and the row waits
 * for the subscription echo; `error` = the mutation rejected.
 */
export type PendingSendStatus = 'sending' | 'sent' | 'error';

export type PendingSendPayload = ChannelComposerSendPayload & {
  replyToMessageId?: Id<'channelMessages'>;
};

export interface PendingSend {
  clientId: string;
  /** `channel:<id>` or `thread:<id>`; rows only render inside their scope. */
  scope: string;
  body: string;
  attachments: ChannelComposerSendPayload['attachments'];
  createdAt: number;
  status: PendingSendStatus;
  errorCode: SendErrorCode | null;
  payload: PendingSendPayload;
}

export type PendingSendList = readonly PendingSend[];

export function channelSendScope(channelId: string): string {
  return `channel:${channelId}`;
}

export function threadSendScope(orgThreadId: string): string {
  return `thread:${orgThreadId}`;
}

export function addPendingSend(
  list: PendingSendList,
  entry: PendingSend
): PendingSendList {
  if (list.some((item) => item.clientId === entry.clientId)) return list;
  return [...list, entry];
}

export function updatePendingSend(
  list: PendingSendList,
  clientId: string,
  patch: Partial<Pick<PendingSend, 'status' | 'errorCode'>>
): PendingSendList {
  let changed = false;
  const next = list.map((item) => {
    if (item.clientId !== clientId) return item;
    changed = true;
    return { ...item, ...patch };
  });
  return changed ? next : list;
}

export function removePendingSend(
  list: PendingSendList,
  clientId: string
): PendingSendList {
  const next = list.filter((item) => item.clientId !== clientId);
  return next.length === list.length ? list : next;
}

/** Drops every entry of `scope` whose clientId the server already echoed. */
export function prunePendingEchoes(
  list: PendingSendList,
  scope: string,
  echoed: ReadonlySet<string>
): PendingSendList {
  const next = list.filter(
    (item) => item.scope !== scope || !echoed.has(item.clientId)
  );
  return next.length === list.length ? list : next;
}

/** Rows for one transcript: in scope and not yet replaced by the echo. */
export function selectVisiblePending(
  list: PendingSendList,
  scope: string | null,
  echoed: ReadonlySet<string>
): PendingSend[] {
  if (!scope) return [];
  return list.filter(
    (item) => item.scope === scope && !echoed.has(item.clientId)
  );
}

/** True while any send has not reached the server (offline queue included). */
export function hasQueuedSends(list: PendingSendList): boolean {
  return list.some((item) => item.status === 'sending');
}

export interface PendingSendStore {
  getSnapshot: () => PendingSendList;
  subscribe: (listener: () => void) => () => void;
  add: (entry: PendingSend) => void;
  update: (
    clientId: string,
    patch: Partial<Pick<PendingSend, 'status' | 'errorCode'>>
  ) => void;
  remove: (clientId: string) => void;
  get: (clientId: string) => PendingSend | undefined;
  pruneEchoes: (scope: string, echoed: ReadonlySet<string>) => void;
}

/**
 * Module-level so optimistic rows survive the channel view remounting on a
 * channel switch and the thread panel remounting per thread: a failed send in
 * one conversation is still there, with Retry, when the viewer comes back.
 */
export function createPendingSendStore(): PendingSendStore {
  let state: PendingSendList = [];
  const listeners = new Set<() => void>();
  const commit = (next: PendingSendList) => {
    if (next === state) return;
    state = next;
    for (const listener of listeners) listener();
  };
  return {
    getSnapshot: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    add: (entry) => commit(addPendingSend(state, entry)),
    update: (clientId, patch) =>
      commit(updatePendingSend(state, clientId, patch)),
    remove: (clientId) => commit(removePendingSend(state, clientId)),
    get: (clientId) => state.find((item) => item.clientId === clientId),
    pruneEchoes: (scope, echoed) =>
      commit(prunePendingEchoes(state, scope, echoed)),
  };
}

export const pendingSendStore = createPendingSendStore();
